// @ts-nocheck
import { Suspense } from 'react'

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'

import { lazyWithRetry as lazy } from '@/utils/lazyWithRetry'

// Layout routes (use Outlet pattern)
import ProtectedRoute from '@/components/ProtectedRoute'
import GuestRoute from '@/components/GuestRoute'
import DashboardLayout from '@/layouts/DashboardLayout'
import AuthLayout from '@/layouts/AuthLayout'
import { ProtectedRoute as ModuleProtectedRoute } from '@/components/rbac/ProtectedRoute'
import { Module } from '@/types/rbac'
import { useAuth } from '@/contexts/AuthContext'
import { getDefaultRoute } from '@/utils/rbac'
import { getCountryBasename } from '@/utils/country'

// ---------- Loading fallback ----------
const Loading = () => (
  <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
    <CircularProgress />
  </Box>
)

// Redirects to the first route the logged-in user actually has permission to
// see, instead of assuming everyone can view /dashboard. Unauthenticated
// visitors fall through to the login page, same as ProtectedRoute below.
const DefaultRedirect = () => {
  const { isAuthenticated, permissions, user, loading } = useAuth()

  if (loading) return <Loading />

  if (!isAuthenticated) return <Navigate to='/en/login' replace />

  return <Navigate to={getDefaultRoute(permissions, user?.role)} replace />
}

// ---------- Lazy page components ----------

// Auth pages
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/views/Register'))
const ForgotPasswordPage = lazy(() => import('@/views/ForgotPassword'))
const NotAuthorizedPage = lazy(() => import('@/views/NotAuthorized'))
const NotFoundPage = lazy(() => import('@/views/NotFound'))

// Dashboard
const DashboardView = lazy(() => import('@/views/dashboards/ecommerce/DashboardView'))

// Orders & Reports
const OrdersView = lazy(() => import('@/views/orders/OrdersView'))
const OrderHistoryView = lazy(() => import('@/views/orders/OrderHistoryView'))
const ReportsView = lazy(() => import('@/views/reports/ReportsView'))

// Marketing
const IntroAdView = lazy(() => import('@/views/marketing/IntroAdView'))
const SlidesView = lazy(() => import('@/views/marketing/SlidesView'))
const ArchivedSlidesView = lazy(() => import('@/views/marketing/ArchivedSlidesView'))
const ReelsView = lazy(() => import('@/views/marketing/ReelsView'))
const ArchivedReelsView = lazy(() => import('@/views/marketing/ArchivedReelsView'))
const PushCampaignsView = lazy(() => import('@/views/marketing/PushCampaignsView'))
const ArchivedCampaignsView = lazy(() => import('@/views/marketing/ArchivedCampaignsView'))
const EmailCampaignsView = lazy(() => import('@/views/marketing/EmailCampaignsView'))
const ArchivedEmailCampaignsView = lazy(() => import('@/views/marketing/ArchivedEmailCampaignsView'))

// Catalog
const ProductsView = lazy(() => import('@/views/catalog/ProductsView'))
const AddProduct = lazy(() => import('@/views/catalog/AddProduct'))
const EditsView = lazy(() => import('@/views/catalog/EditsView'))
const ArchivedProductsView = lazy(() => import('@/views/catalog/ArchivedProductsView'))
const CategoriesView = lazy(() => import('@/views/catalog/CategoriesView'))
const ArchivedCategoriesView = lazy(() => import('@/views/catalog/ArchivedCategoriesView'))
const SubcategoriesView = lazy(() => import('@/views/catalog/SubcategoriesView'))
const BrandsView = lazy(() => import('@/views/catalog/BrandsView'))
const ArchivedBrandsView = lazy(() => import('@/views/catalog/ArchivedBrandsView'))
const AttributesManagementView = lazy(() => import('@/views/catalog/AttributesManagementView'))
const OffersView = lazy(() => import('@/views/catalog/OffersView'))
const ReviewsView = lazy(() => import('@/views/catalog/ReviewsView'))
const ArchivedReviewsView = lazy(() => import('@/views/catalog/ArchivedReviewsView'))

// Users
const UsersView = lazy(() => import('@/views/users/UsersView'))
const RolesView = lazy(() => import('@/views/users/RolesView'))
const PermissionsView = lazy(() => import('@/views/users/PermissionsView'))
const PermissionCategoriesView = lazy(() => import('@/views/users/PermissionCategoriesView'))

// Customers
const CustomersView = lazy(() => import('@/views/customers/CustomersView'))
const CustomerProfileView = lazy(() => import('@/views/customers/CustomerProfileView'))
const ArchivedCustomersView = lazy(() => import('@/views/customers/ArchivedCustomersView'))

// Payments
const PaymentMethodsView = lazy(() => import('@/views/payments/PaymentMethodsView'))
const WalletView = lazy(() => import('@/views/payments/WalletView'))
const WebhooksView = lazy(() => import('@/views/payments/WebhooksView'))
const FreeShippingView = lazy(() => import('@/views/payments/FreeShippingView'))
const PaymentTransactionsView = lazy(() => import('@/views/payments/PaymentTransactionsView'))
const EditProfilePage = lazy(() => import('@/views/profile/EditProfilePage'))
const PromoCodesView = lazy(() => import('@/views/promo-codes/PromoCodesView'))
const ArchivedPromoCodesView = lazy(() => import('@/views/promo-codes/ArchivedPromoCodesView'))

// Shipments
const ShipmentsView = lazy(() => import('@/views/shipments/ShipmentsView'))
const ShippingCompaniesView = lazy(() => import('@/views/shipping/ShippingCompaniesView'))

// CMS
const HelpInfoView = lazy(() => import('@/views/cms/HelpInfoView'))
const TermsOfServiceView = lazy(() => import('@/views/cms/TermsOfServiceView'))
const PrivacyPolicyView = lazy(() => import('@/views/cms/PrivacyPolicyView'))
const ContactUsView = lazy(() => import('@/views/cms/ContactUsView'))

// Support & Tickets
const SupportView = lazy(() => import('@/views/support/SupportView'))
const TicketsView = lazy(() => import('@/views/tickets/TicketsView'))

// Settings
const SettingsView = lazy(() => import('@/views/settings/SettingsView'))
const PaymentOptionsView = lazy(() => import('@/views/settings/PaymentOptionsView'))
const SenderSettingsView = lazy(() => import('@/views/settings/SenderSettingsView'))
const ColorsView = lazy(() => import('@/views/settings/ColorsView'))
const SizesView = lazy(() => import('@/views/settings/SizesView'))
const MaintenanceModeView = lazy(() => import('@/views/settings/MaintenanceModeView'))
const SmtpSettingsView = lazy(() => import('@/views/settings/SmtpSettingsView'))

// Alerts
const SystemAlertsView = lazy(() => import('@/views/alerts/SystemAlertsView'))

// Activity Log
const ActivityLogView = lazy(() => import('@/views/activity-log/ActivityLogView'))

// OTP
const EmailAuthView = lazy(() => import('@/views/otp/EmailAuthView'))
const SMSOTPView = lazy(() => import('@/views/otp/SMSOTPView'))

// ---------- Suspense wrapper helper ----------
const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
)

// ---------- Router definition ----------
export const router = createBrowserRouter([
  // Root redirect
  { path: '/', element: <S><DefaultRedirect /></S> },

  // Guest-only auth routes (redirect to dashboard if already logged in)
  {
    element: <GuestRoute />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: '/en/login', element: <S><LoginPage /></S> },
          { path: '/en/register', element: <S><RegisterPage /></S> },
          { path: '/en/forgot-password', element: <S><ForgotPasswordPage /></S> }
        ]
      }
    ]
  },

  // Public error / misc pages (no auth guard) — wrapped in AuthLayout so
  // SettingsProvider/ThemeProvider are available (NotAuthorized uses
  // useImageVariant -> useSettingsContext).
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/en/pages/misc/401-not-authorized',
        element: <S><NotAuthorizedPage mode='light' /></S>
      }
    ]
  },

  // Protected dashboard routes
  {
    path: '/en',
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { index: true, element: <S><DefaultRedirect /></S> },
          {
            path: 'dashboard',
            element: (
              <S>
                <ModuleProtectedRoute requiredModule={Module.DASHBOARD}>
                  <DashboardView />
                </ModuleProtectedRoute>
              </S>
            )
          },

          // Maintenance Mode
          { path: 'maintenance-mode', element: <S><MaintenanceModeView /></S> },

          // Orders & Reports
          { path: 'orders', element: <S><OrdersView /></S> },
          { path: 'orders/:type/:id/history', element: <S><OrderHistoryView /></S> },
          { path: 'reports', element: <S><ReportsView /></S> },

          // Marketing
          { path: 'marketing/intro-ad', element: <S><IntroAdView /></S> },
          { path: 'marketing/slides', element: <S><SlidesView /></S> },
          { path: 'marketing/slides-tags', element: <S><SlidesView place='tags' /></S> },
          { path: 'marketing/slides-categories', element: <S><SlidesView place='categories' /></S> },
          { path: 'marketing/slides-mini-categories', element: <S><SlidesView place='mini_categories' /></S> },
          { path: 'marketing/slides-brands', element: <S><SlidesView place='brands' /></S> },
          { path: 'marketing/slides/archived', element: <S><ArchivedSlidesView /></S> },
          { path: 'marketing/reels', element: <S><ReelsView /></S> },
          { path: 'marketing/reels/archived', element: <S><ArchivedReelsView /></S> },
          { path: 'marketing/push-campaigns', element: <S><PushCampaignsView /></S> },
          { path: 'marketing/push-campaigns/archived', element: <S><ArchivedCampaignsView /></S> },
          { path: 'marketing/email-campaigns', element: <S><EmailCampaignsView /></S> },
          { path: 'marketing/email-campaigns/archived', element: <S><ArchivedEmailCampaignsView /></S> },

          // Catalog
          { path: 'catalog/products', element: <S><ProductsView /></S> },
          { path: 'catalog/products/add', element: <S><AddProduct /></S> },
          { path: 'catalog/products/edit', element: <S><EditsView /></S> },
          { path: 'catalog/products/archived', element: <S><ArchivedProductsView /></S> },
          { path: 'catalog/categories', element: <S><CategoriesView /></S> },
          { path: 'catalog/categories/archived', element: <S><ArchivedCategoriesView /></S> },
          { path: 'catalog/subcategories', element: <S><SubcategoriesView /></S> },
          { path: 'catalog/brands', element: <S><BrandsView /></S> },
          { path: 'catalog/brands/archived', element: <S><ArchivedBrandsView /></S> },
          { path: 'catalog/attributes', element: <S><AttributesManagementView /></S> },
          { path: 'catalog/offers', element: <S><OffersView /></S> },
          { path: 'catalog/reviews', element: <S><ReviewsView /></S> },
          { path: 'catalog/reviews/archived', element: <S><ArchivedReviewsView /></S> },

          // Users
          { path: 'users', element: <S><UsersView /></S> },
          { path: 'users/roles', element: <S><RolesView /></S> },
          { path: 'users/permissions', element: <S><PermissionsView /></S> },
          { path: 'users/permission-categories', element: <S><PermissionCategoriesView /></S> },

          // Customers
          { path: 'customers', element: <S><CustomersView /></S> },
          { path: 'customers/archived', element: <S><ArchivedCustomersView /></S> },
          { path: 'customers/:id', element: <S><CustomerProfileView /></S> },

          // Payments
          { path: 'payments/methods', element: <S><PaymentMethodsView /></S> },
          { path: 'payments/wallet', element: <S><WalletView /></S> },
          { path: 'payments/webhooks', element: <S><WebhooksView /></S> },
          { path: 'payments/shipping-threshold', element: <S><FreeShippingView /></S> },
          { path: 'payments/options', element: <S><PaymentOptionsView /></S> },
          { path: 'payments/transactions', element: <S><PaymentTransactionsView /></S> },
          { path: 'profile', element: <S><EditProfilePage /></S> },
          { path: 'promo-codes', element: <S><PromoCodesView /></S> },
          { path: 'promo-codes/archived', element: <S><ArchivedPromoCodesView /></S> },

          // Shipments
          { path: 'shipments/sender-settings', element: <S><SenderSettingsView /></S> },
          { path: 'shipments', element: <S><ShipmentsView /></S> },
          { path: 'shipments/companies', element: <S><ShippingCompaniesView /></S> },

          // Eilik Local Team
          { path: 'local-team', element: <S><OrdersView shippingTypeFilter='local' /></S> },

          // CMS
          { path: 'cms/help', element: <S><HelpInfoView /></S> },
          { path: 'cms/terms', element: <S><TermsOfServiceView /></S> },
          { path: 'cms/privacy', element: <S><PrivacyPolicyView /></S> },
          { path: 'cms/contact-us', element: <S><ContactUsView /></S> },

          // Support & Tickets
          { path: 'support', element: <S><SupportView /></S> },
          { path: 'tickets', element: <S><TicketsView /></S> },

          // Settings
          { path: 'settings', element: <S><SettingsView /></S> },
          { path: 'settings/colors', element: <S><ColorsView /></S> },
          { path: 'settings/sizes', element: <S><SizesView /></S> },
          { path: 'settings/smtp', element: <S><SmtpSettingsView /></S> },

          // System Alerts
          { path: 'system-alerts', element: <S><SystemAlertsView /></S> },

          // Activity Log
          { path: 'activity-log', element: <S><ActivityLogView /></S> },

          // OTP
          { path: 'otp/email', element: <S><EmailAuthView /></S> },
          { path: 'otp/sms', element: <S><SMSOTPView /></S> }
        ]
      }
    ]
  },

  // Catch-all 404 — wrapped in AuthLayout so SettingsProvider/ThemeProvider are available
  {
    element: <AuthLayout />,
    children: [
      {
        path: '*',
        element: <S><NotFoundPage mode='light' /></S>
      }
    ]
  }
], {
  // Every country lives under its own prefix: /sa, /kw, ...
  basename: getCountryBasename()
})

// Convenience component for use in App.tsx
export const AppRouter = () => <RouterProvider router={router} />
